import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, Info, Plane } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface AirportProperties {
  id: string;
  name: string | null;
  operator: string | null;
  county: string | null;
  state: string | null;
  status: string | null;
  metadata: Record<string, unknown> | null;
}

interface AirportDetailProps {
  airport: AirportProperties;
  onClose: () => void;
}

interface RunwayRow {
  id: string | null;
  len_ft: number;
  width_ft: number | null;
  surface: string | null;
  lit: boolean;
  cond: string | null;
  pcn: number | null;
}

const SURFACE_LABELS: Record<string, string> = {
  ASPH: 'Asphalt',
  CONC: 'Concrete',
  'ASPH-CONC': 'Asphalt over concrete',
  'CONC-ASPH': 'Concrete over asphalt',
  TURF: 'Turf',
  GRVL: 'Gravel',
  DIRT: 'Dirt',
  WATER: 'Water',
};

const TOWER_LABELS: Record<string, string> = {
  ATCT: 'Control tower',
  'ATCT-TRACON': 'Tower + approach radar',
  NONFEDERAL: 'Non-federal tower',
};

const OWNERSHIP_LABELS: Record<string, string> = {
  PU: 'Publicly owned',
  PR: 'Privately owned',
  MA: 'Air Force',
  MN: 'Navy',
  MR: 'Army',
  CG: 'Coast Guard',
};

const REPAIR_LABELS: Record<string, string> = {
  MAJOR: 'Major',
  MINOR: 'Minor',
  NONE: 'None',
};

function tierForRunway(maxPavedFt: number | null) {
  if (maxPavedFt == null || maxPavedFt < 5000) {
    return { label: 'GA strip', color: '#6A7E8E', desc: 'Below 5,000 ft paved — general-aviation only. Most corporate jets and turboprop heavy-lift cannot use this strip.' };
  }
  if (maxPavedFt >= 10000) {
    return { label: 'Heavy cargo capable', color: '#15803d', desc: 'Paved runway ≥10,000 ft — handles widebody freighters (747-F, An-124). Site directly serviceable for transformer / pressure-vessel jumbo lifts.' };
  }
  if (maxPavedFt >= 7000) {
    return { label: 'Heavy lift capable', color: '#16a34a', desc: 'Paved runway ≥7,000 ft — accepts C-130 / L-100 turboprop freighters and large business jets. Suitable for time-critical OEM parts.' };
  }
  return { label: 'Corp-jet capable', color: '#0891b2', desc: 'Paved runway ≥5,000 ft — supports most corporate jets (Citation, Falcon, Gulfstream G450 class). Investor tours and OEM field-tech crews can fly direct.' };
}

const yn = (v: unknown) => v === 'Y' ? 'Yes' : v === 'N' ? 'No' : null;

export const AirportDetail: React.FC<AirportDetailProps> = ({ airport, onClose }) => {
  const { theme } = useTheme();
  const [sourceExpanded, setSourceExpanded] = useState(false);
  const a = airport;
  const meta = (typeof a.metadata === 'string' ? JSON.parse(a.metadata) : (a.metadata || {})) as Record<string, unknown>;

  const arptId = typeof meta.arpt_id === 'string' ? meta.arpt_id : null;
  const icaoId = typeof meta.icao_id === 'string' ? meta.icao_id : null;
  const city = typeof meta.city === 'string' ? meta.city : null;
  const airportClass = meta.airport_class === 'commercial' ? 'commercial' : 'general_aviation';
  const far139 = typeof meta.far_139_type === 'string' && meta.far_139_type.trim() ? meta.far_139_type.trim() : null;
  const towerType = typeof meta.tower_type === 'string' && meta.tower_type.trim() ? meta.tower_type.trim() : null;
  const elevationFt = typeof meta.elevation_ft === 'number' ? meta.elevation_ft : null;
  const acreage = typeof meta.acreage === 'number' ? meta.acreage : null;
  const naspCode = typeof meta.nasp_code === 'string' && meta.nasp_code.trim() ? meta.nasp_code.trim() : null;

  const runways = Array.isArray(meta.runways) ? (meta.runways as RunwayRow[]) : [];
  const runwayCount = typeof meta.runway_count === 'number' ? meta.runway_count : runways.length;
  const maxPaved = typeof meta.max_paved_runway_ft === 'number' ? meta.max_paved_runway_ft : null;
  const maxLit = typeof meta.max_lit_runway_ft === 'number' ? meta.max_lit_runway_ft : null;
  const tier = tierForRunway(maxPaved);

  // Ops fields
  const fuelTypes = typeof meta.fuel_types === 'string' && meta.fuel_types.trim() ? meta.fuel_types.trim() : null;
  const customs = yn(meta.customs);
  const landingRights = yn(meta.landing_rights);
  const militaryLanding = yn(meta.military_landing);
  const jointUse = yn(meta.joint_use);
  const landingFee = yn(meta.landing_fee);
  const airframeRepair = typeof meta.airframe_repair === 'string' && meta.airframe_repair.trim() ? meta.airframe_repair.trim() : null;
  const powerplantRepair = typeof meta.powerplant_repair === 'string' && meta.powerplant_repair.trim() ? meta.powerplant_repair.trim() : null;
  const lightingSchedule = typeof meta.lighting_schedule === 'string' && meta.lighting_schedule.trim() ? meta.lighting_schedule.trim() : null;
  const beaconSchedule = typeof meta.beacon_schedule === 'string' && meta.beacon_schedule.trim() ? meta.beacon_schedule.trim() : null;

  const ownership = a.operator && OWNERSHIP_LABELS[a.operator] ? OWNERSHIP_LABELS[a.operator] : a.operator;

  const hasOpsData = fuelTypes || customs || landingRights || militaryLanding || jointUse || landingFee || airframeRepair || powerplantRepair || lightingSchedule || beaconSchedule;

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{
            fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {a.name || 'Airport'}
          </h2>
          <div style={{ marginTop: '4px', fontSize: '11px', color: theme.textSecondary, fontFamily: designTokens.typography.fontFamily.mono }}>
            {[icaoId, arptId].filter(Boolean).join(' · ')}
            {city && (icaoId || arptId) ? ' · ' : ''}
            {city ? `${city.replace(/\b\w/g, c => c.toUpperCase())}${a.state ? `, ${a.state}` : ''}` : ''}
          </div>
          <div style={{ marginTop: '6px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <span style={{
              padding: '2px 6px', fontSize: '9px', fontWeight: 600,
              background: tier.color + '20', color: tier.color, borderRadius: '4px',
            }}>
              <Plane size={9} style={{ marginRight: '3px', verticalAlign: '-1px' }} />
              {tier.label}
            </span>
            {airportClass === 'commercial' && (
              <span style={{
                padding: '2px 6px', fontSize: '9px', fontWeight: 600,
                background: '#ef444420', color: '#ef4444', borderRadius: '4px',
              }}>Commercial</span>
            )}
            {far139 && (
              <span style={{
                padding: '2px 6px', fontSize: '9px', fontWeight: 600,
                background: '#0e749020', color: '#0e7490', borderRadius: '4px',
              }}>FAR-139 {far139}</span>
            )}
            {towerType && (
              <span style={{
                padding: '2px 6px', fontSize: '9px', fontWeight: 600,
                background: '#6b728020', color: '#6b7280', borderRadius: '4px',
              }}>Towered</span>
            )}
            {customs === 'Yes' && (
              <span style={{
                padding: '2px 6px', fontSize: '9px', fontWeight: 600,
                background: '#7c3aed20', color: '#7c3aed', borderRadius: '4px',
              }}>Customs</span>
            )}
            {fuelTypes && /JET[\s-]?A/i.test(fuelTypes) && (
              <span style={{
                padding: '2px 6px', fontSize: '9px', fontWeight: 600,
                background: '#d9770620', color: '#d97706', borderRadius: '4px',
              }}>Jet A</span>
            )}
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Heavy-Lift Assessment */}
        <Section title="Heavy-Lift & Crew Access">
          <div style={{
            padding: '10px', borderRadius: '6px',
            background: tier.color + '10', border: `1px solid ${tier.color}30`,
            marginBottom: '8px',
          }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: tier.color, marginBottom: '4px' }}>
              {tier.label}
            </div>
            <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.5 }}>
              {tier.desc}
            </div>
            {maxPaved != null && (
              <div style={{ marginTop: '6px', fontSize: '11px', color: theme.text }}>
                Longest paved: <strong>{maxPaved.toLocaleString()} ft</strong>
                {maxLit != null && maxLit > 0 && maxLit !== maxPaved && (
                  <>, lit max <strong>{maxLit.toLocaleString()} ft</strong></>
                )}
              </div>
            )}
          </div>
        </Section>

        {/* Runways */}
        {runways.length > 0 && (
          <Section title={`Runways (${runwayCount})`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {runways.map((r, idx) => {
                const surfaceLabel = r.surface ? (SURFACE_LABELS[r.surface] || r.surface) : null;
                const isPaved = r.surface && /^(ASPH|CONC)/.test(r.surface);
                return (
                  <div key={`${r.id}-${idx}`} style={{
                    padding: '8px 10px', borderRadius: '6px',
                    border: `1px solid ${theme.borderLight}`,
                    background: isPaved ? `${tier.color}08` : 'transparent',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.mono,
                        fontSize: '13px', fontWeight: 700, color: theme.text,
                      }}>{r.id || '—'}</span>
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.mono,
                        fontSize: '12px', fontWeight: 600, color: theme.text,
                      }}>
                        {r.len_ft.toLocaleString()} ft{r.width_ft ? ` × ${r.width_ft} ft` : ''}
                      </span>
                    </div>
                    <div style={{ marginTop: '3px', fontSize: '10px', color: theme.textSecondary, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {surfaceLabel && <span>{surfaceLabel}</span>}
                      {r.lit && <span style={{ color: '#d97706' }}>Lit</span>}
                      {r.cond && <span>{r.cond}</span>}
                      {r.pcn != null && <span>PCN {r.pcn}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Air Ops */}
        {hasOpsData && (
          <Section title="Air Operations">
            {fuelTypes && <Field label="Fuel available" value={fuelTypes.replace(/,/g, ', ')} />}
            {customs && <Field label="Customs" value={customs} />}
            {landingRights && <Field label="Landing rights required" value={landingRights} />}
            {militaryLanding && <Field label="Military landings" value={militaryLanding} />}
            {jointUse && <Field label="Joint civil/military" value={jointUse} />}
            {landingFee && <Field label="Landing fee" value={landingFee} />}
            {airframeRepair && <Field label="Airframe repair" value={REPAIR_LABELS[airframeRepair] || airframeRepair} />}
            {powerplantRepair && <Field label="Powerplant repair" value={REPAIR_LABELS[powerplantRepair] || powerplantRepair} />}
            {lightingSchedule && <Field label="Runway lighting hours" value={lightingSchedule} />}
            {beaconSchedule && <Field label="Beacon hours" value={beaconSchedule} />}
          </Section>
        )}

        {/* Facility */}
        <Section title="Facility">
          {elevationFt != null && (
            <Field label="Elevation" value={`${Math.round(elevationFt).toLocaleString()} ft MSL`} />
          )}
          {acreage != null && acreage > 0 && (
            <Field label="Airport acreage" value={`${Number(acreage).toLocaleString()} ac`} />
          )}
          {towerType && (
            <Field label="Tower" value={TOWER_LABELS[towerType] || towerType} />
          )}
          {naspCode && <Field label="NPIAS code" value={naspCode} />}
          {ownership && <Field label="Ownership" value={ownership} />}
          {a.county && <Field label="County" value={`${a.county} Co.`} />}
          {a.state && <Field label="State" value={a.state} />}
        </Section>

        {/* Data Source — expandable */}
        <div style={{ marginTop: '8px' }}>
          <button
            onClick={() => setSourceExpanded(!sourceExpanded)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '2px 8px', fontSize: '10px', fontWeight: 600,
              background: '#4b556320', color: '#8A9CAA',
              borderRadius: '4px', border: 'none', cursor: 'pointer',
            }}
          >
            <Info size={10} />
            Data Sources
            {sourceExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </button>
          {sourceExpanded && (
            <div style={{
              marginTop: '6px', padding: '8px', fontSize: '10px',
              border: `1px solid ${theme.borderLight}`,
              borderRadius: '6px', lineHeight: 1.6,
            }}>
              <div style={{ color: theme.text, fontWeight: 600, marginBottom: '4px' }}>
                FAA National Transportation Atlas Database (NTAD)
              </div>
              <div style={{ color: theme.textTertiary }}>
                Aviation Facilities feature service — airport identity, ownership, FAR-139 certification, tower type, services, and elevation. Runway lengths, widths, surfaces, and lighting from the companion Runways feature service. Filtered to public-use civilian airports.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
